import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Alert, Anchor, Box, Heading, Modal, Text } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing } from '@zg-rentals/rental-manager-env';

import { RMWContext } from '../../../providers/RMWContext';

import { PropertiesSelectors } from '../../../selectors';

import LeadActions from '../../../actions/LeadActions';
import PostingActions from '../../../actions/PostingActions';
import PropertiesActions from '../../../actions/PropertiesActions';

import DispatchConstants from '../../../constants/DispatchConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import { STEP as CLP_STEP } from '../../../containers/CreateListingPage/constants';

import DeactivationModalCSEvents, {
  TRIGGER_SOURCE_MAP,
} from '../../../model/ClickstreamEvent/DeactivationModalCSEvents';
import FindTenantsPageCSEvents from '../../../model/ClickstreamEvent/FindTenantsPageCSEvents';
import ReviewPageCSEvents from '../../../model/ClickstreamEvent/ReviewPageCSEvents';

import ModalFooter from '../../../components/Modal/ModalFooter';

import { useSubmitDeactivationLeasesSurvey } from '../../../hooks/queries/deactivate';
import { useCreateNewLease } from '../../../hooks/queries/leasing';
import { useGhostedLeads, useNotAvailableMessage } from './hooks';

import {
  CREATE_LEASE_CONTENT,
  LAUNCHED_FROM_OPTIONS,
  LEASE_CREATION_CTA_STEPS,
  LEASES_SURVEY_CONTENT,
  LEASES_SURVEY_STEPS,
  REASONS,
  STEPS,
  submitButtonText,
} from './constants';

import CreateLease from './components/CreateLease';
import DeactivationReason from './components/DeactivationReason';
import Congratulations from './components/Congratulations';
import NotifyLeads from './components/NotifyLeads';
import LeasesSurvey from './components/LeasesSurvey';
import { isValid } from 'date-fns';

const { SYNDICATION_OVERVIEW_STATUS, LISTING_STATUS } = PropertyConstants;

const csEventMapper = {
  [LAUNCHED_FROM_OPTIONS.SOH]: FindTenantsPageCSEvents.StartDeactivation,
  [LAUNCHED_FROM_OPTIONS.EDIT_LISTING_PAGE]: ReviewPageCSEvents.StartDeactivation,
};

const DeactivationModal = ({ setDisplayDeactivateModal, launchedFrom }) => {
  const { paths } = useContext(RMWContext);

  const dispatch = useDispatch();

  /**
   * NOTE: needed to handle `/:propertyId/listing-availability`
   */
  const { propertyId } = useParams();

  const [step, setStep] = useState(STEPS.DEACTIVATION_REASON);
  const [feedback, setFeedback] = useState();
  const [reason, setReason] = useState();
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldShowLeasesSurvey, setShouldShowLeasesSurvey] = useState(false);
  const [isLeaseBuilderAllowed, setIsLeaseBuilderAllowed] = useState(false);
  const [leaseSurveySelections, setLeaseSurveySelections] = useState({
    haveSignedLease: undefined,
    haveLeaseAgreement: undefined,
  });

  const queryClient = useQueryClient();

  const { currentProperty, user } = useSelector(
    (state) => ({ app: state.app, currentProperty: state.properties.currentProperty, user: state.user }),
    shallowEqual,
  );

  const {
    listingAlias: currentPropertyListingAlias,
    expirationDateTimestamp,
    syndication,
    street,
    contactName,
    isMultifamily,
  } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const listingAlias = useMemo(() => {
    return currentPropertyListingAlias || propertyId;
  }, [propertyId, currentPropertyListingAlias]);

  const { mutate: deactivateListing, isLoading } =
    useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing();

  const { mutate: postLeasesSurveyResponse } = useSubmitDeactivationLeasesSurvey();
  const { mutate: postCreateNewLease } = useCreateNewLease({
    onError: () => {
      setStep(STEPS.CREATE_LEASE_ERROR);
    },
    onSuccess: (response) => {
      const leasePathSuffix = CREATE_LEASE_CONTENT[step].LEASE_PATH_SUFFIX;
      const manageLeasePath = `${paths.leasing}/property/${listingAlias}/lease/${response.leaseId}/${leasePathSuffix}`;
      window.router.updateLocation(manageLeasePath, '_self');
    },
  });

  const { ghostedLeads, toggleIncludeLead, isFetchingGhostedLeads } = useGhostedLeads(listingAlias, isMultifamily);
  const { notAvailableMessage, setNotAvailableMessage } = useNotAvailableMessage(street, contactName);

  const isCreateLeaseCTAStep = LEASE_CREATION_CTA_STEPS.includes(step);
  const isLeasesSurveyStep = LEASES_SURVEY_STEPS.includes(step);

  const heading = useMemo(() => {
    if (step === STEPS.DEACTIVATION_REASON || step === STEPS.NOTIFY_LEADS) {
      return 'Deactivate listing';
    } else if (step === STEPS.CONGRATULATIONS) {
      return 'Thanks!';
    } else if (step === STEPS.LEASES_SURVEY) {
      return 'Lease status';
    } else {
      return shouldShowLeasesSurvey ? 'Lease management' : 'Listing deactivated';
    }
  }, [step]);

  const cancelText = useMemo(() => {
    if (step === STEPS.DEACTIVATION_REASON) {
      return 'Cancel';
    } else if (isCreateLeaseCTAStep) {
      return 'No thanks';
    } else {
      return 'Skip';
    }
  }, [step, isCreateLeaseCTAStep]);

  const hideFooter = step === STEPS.CREATE_LEASE_ERROR || step === STEPS.THANK_YOU;

  const formattedExpirationDate =
    isValid(new Date(Number(expirationDateTimestamp))) &&
    format(new Date(Number(expirationDateTimestamp)), 'MMMM d, yyyy');

  const submitText =
    (shouldShowLeasesSurvey && step === STEPS.CONGRATULATIONS) || isLeasesSurveyStep ? 'Next' : submitButtonText[step];

  const csEventParams = useMemo(() => ({ user, currentProperty }), [user, currentProperty]);

  useEffect(() => {
    csEventMapper[launchedFrom]?.(csEventParams);
  }, []);

  const updateCurrentProperty = () => {
    const dataToUpdate = {
      active: false,
      expirationDateTimestamp: Date.now(),
      status: 'OFF-MARKET',
      syndication: {
        ...syndication,
        overview: {
          status: SYNDICATION_OVERVIEW_STATUS.INACTIVE,
          displayStatus: LISTING_STATUS.INACTIVE,
          publishedCount: 0,
        },
      },
    };
    dispatch(PropertiesActions.updateCurrentProperty(dataToUpdate));

    dispatch({
      type: DispatchConstants.UPDATE_PROPERTY_OVERVIEW,
      payload: dataToUpdate,
    });
  };

  const handleDeactivateListingAndSubmitFeedback = async () => {
    deactivateListing(
      {
        postData: {
          commentStr: feedback,
          listingAlias,
          reason,
        },
      },
      {
        onSuccess: (response) => {
          const {
            leasesStatus: { showLeasesSurvey, isLeaseBuilderAllowed },
          } = response;
          setShouldShowLeasesSurvey(showLeasesSurvey);
          setIsLeaseBuilderAllowed(isLeaseBuilderAllowed);
          const foundTenant = reason !== REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW && reason !== REASONS.OTHER;
          const nextStep = showLeasesSurvey && foundTenant ? STEPS.LEASES_SURVEY : STEPS.THANK_YOU;
          const shouldNotifyLeads =
            !isMultifamily &&
            step === STEPS.DEACTIVATION_REASON &&
            reason !== REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW &&
            ghostedLeads.length > 0;
          setStep(shouldNotifyLeads ? STEPS.NOTIFY_LEADS : nextStep);
          updateCurrentProperty();

          queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
          queryClient.invalidateQueries({ queryKey: ['getListingSummary', listingAlias] });
          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: () => {
          setError(true);
        },
        onSettled: () => {
          dispatch(
            PostingActions.property.modularListingProgress({
              propertyId: listingAlias,
              step: CLP_STEP.REVIEW.toLowerCase(),
            }),
          );
        },
      },
    );
  };

  const handleFeedbackChange = (e) => setFeedback(e.target.value);

  const handleNotifyLeads = async () => {
    const requests = ghostedLeads.map(({ conversationId, isIncluded }) => {
      if (isIncluded) {
        return dispatch(LeadActions.addMessage({ conversationId, message: notAvailableMessage }));
      }
      return Promise.resolve(true);
    });
    return Promise.all(requests)
      .then(() => setStep(STEPS.CONGRATULATIONS))
      .catch(() => setError(true));
  };

  const handleSendLeasesSurveyResponse = (step) => {
    const type = LEASES_SURVEY_CONTENT[step].DB_VALUE;
    const value = typeof leaseSurveySelections[type] === 'boolean' ? leaseSurveySelections[type] : false;
    postLeasesSurveyResponse({
      listingAlias,
      type,
      value,
    });
  };

  const handleSendLeaseCTAResponse = (step, didClickConfirm) => {
    const type = CREATE_LEASE_CONTENT[step].DB_VALUE;
    const value = didClickConfirm;
    postLeasesSurveyResponse({
      listingAlias,
      type,
      value,
    });
  };

  const handleLeaseCreationAndRedirectToLeasesPage = async () => {
    const leaseType = CREATE_LEASE_CONTENT[step].LEASE_TYPE;
    postCreateNewLease({ propertyId: listingAlias, leaseType });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      switch (step) {
        case STEPS.CONGRATULATIONS: {
          if (shouldShowLeasesSurvey) {
            setStep(STEPS.LEASES_SURVEY);
          } else {
            setStep(STEPS.THANK_YOU);
          }

          break;
        }
        case STEPS.DEACTIVATION_REASON:
          DeactivationModalCSEvents.Deactivate(csEventParams);
          await handleDeactivateListingAndSubmitFeedback();
          break;
        case STEPS.NOTIFY_LEADS:
          DeactivationModalCSEvents.DynamicNotifyLeads({
            ...csEventParams,
            triggerSource: TRIGGER_SOURCE_MAP.NOTIFY,
          });
          await handleNotifyLeads();
          break;
        case STEPS.LEASES_SURVEY:
          if (leaseSurveySelections.haveSignedLease === true) {
            setStep(STEPS.UPLOAD_SIGNED_LEASE);
          } else {
            if (isLeaseBuilderAllowed) {
              setStep(STEPS.LEASES_SURVEY_NEEDS_LEASE);
            } else {
              setStep(STEPS.UPLOAD_UNSIGNED_LEASE);
            }
          }
          handleSendLeasesSurveyResponse(step);
          break;
        case STEPS.LEASES_SURVEY_NEEDS_LEASE:
          if (leaseSurveySelections.haveLeaseAgreement === true) {
            setStep(STEPS.UPLOAD_UNSIGNED_LEASE);
          } else {
            setStep(STEPS.CREATE_CUSTOM_LEASE);
          }
          handleSendLeasesSurveyResponse(step);
          break;
        case STEPS.CREATE_CUSTOM_LEASE:
        case STEPS.UPLOAD_SIGNED_LEASE:
        case STEPS.UPLOAD_UNSIGNED_LEASE:
          handleSendLeaseCTAResponse(step, true);
          CREATE_LEASE_CONTENT[step].CONFIRM_CS_EVENT({
            ...csEventParams,
          });
          await handleLeaseCreationAndRedirectToLeasesPage(step);
          break;
        default:
          break;
      }
    } finally {
      setIsSubmitting(false);
    }

    return Promise.resolve(true);
  };

  const handleModalClose = () => {
    if (step !== STEPS.DEACTIVATION_REASON) {
      if (isCreateLeaseCTAStep) {
        handleSendLeaseCTAResponse(step, false);
      } else if (isLeasesSurveyStep) {
        handleSendLeasesSurveyResponse(step);
      }
    }
    setDisplayDeactivateModal(false);
  };

  const handleCancelClick = () => {
    setError(false);

    switch (step) {
      case STEPS.DEACTIVATION_REASON:
        // TODO: set step to new "Exit without deactivating" screen (see figma) instead of closing modal
        setDisplayDeactivateModal(false);
        break;
      case STEPS.NOTIFY_LEADS:
        DeactivationModalCSEvents.DynamicNotifyLeads({
          ...csEventParams,
          triggerSource: TRIGGER_SOURCE_MAP.SKIP,
        });
        setStep(STEPS.CONGRATULATIONS);
        break;
      case STEPS.CONGRATULATIONS:
        DeactivationModalCSEvents.SkipFromSummaryStep(csEventParams);
        setDisplayDeactivateModal(false);
        break;
      case STEPS.LEASES_SURVEY:
      case STEPS.LEASES_SURVEY_NEEDS_LEASE:
        handleSendLeasesSurveyResponse(step);
        setStep(STEPS.THANK_YOU);
        break;
      case STEPS.CREATE_CUSTOM_LEASE:
      case STEPS.UPLOAD_SIGNED_LEASE:
      case STEPS.UPLOAD_UNSIGNED_LEASE:
        handleSendLeaseCTAResponse(step, false);
        CREATE_LEASE_CONTENT[step].DECLINE_CS_EVENT({
          ...csEventParams,
        });
        setDisplayDeactivateModal(false);
        break;
      default:
        setDisplayDeactivateModal(false);
    }
  };

  const isNextButtonDisabled = () => {
    switch (step) {
      case STEPS.DEACTIVATION_REASON:
        return reason === undefined || isLoading || isFetchingGhostedLeads;
      case STEPS.NOTIFY_LEADS:
        return ghostedLeads.filter(({ isIncluded }) => isIncluded).length === 0;
      case STEPS.LEASES_SURVEY:
      case STEPS.LEASES_SURVEY_NEEDS_LEASE:
        return typeof leaseSurveySelections[LEASES_SURVEY_CONTENT[step].DB_VALUE] === 'undefined';
      default:
        return false;
    }
  };

  const onSubmitDataEventId = useMemo(() => {
    if (step === STEPS.DEACTIVATION_REASON) {
      return DeactivationModalCSEvents.Deactivate.data_event_id;
    }
    if (step === STEPS.NOTIFY_LEADS) {
      return DeactivationModalCSEvents.DynamicNotifyLeads.data_event_id;
    }
    if (isCreateLeaseCTAStep) {
      return CREATE_LEASE_CONTENT[step]?.CONFIRM_CS_EVENT?.data_event_id;
    }
    return undefined;
  }, [step, isCreateLeaseCTAStep]);

  const onCancelDataEventId = useMemo(() => {
    if (step === STEPS.CONGRATULATIONS) {
      return DeactivationModalCSEvents.SkipFromSummaryStep.data_event_id;
    }
    if (step === STEPS.NOTIFY_LEADS) {
      return DeactivationModalCSEvents.DynamicNotifyLeads.data_event_id;
    }
    if (isCreateLeaseCTAStep) {
      return CREATE_LEASE_CONTENT[step]?.DECLINE_CS_EVENT?.data_event_id;
    }
    return undefined;
  }, [step, isCreateLeaseCTAStep]);

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleModalClose()}
      header={
        <Heading level={5} textAlign="left" data-eventid={csEventMapper[launchedFrom]?.data_event_id}>
          {heading}
        </Heading>
      }
      body={
        <div data-testid="DeactivationModal">
          {error && (
            <Box css={{ marginBottom: '200' }}>
              <Alert tone="critical">There was an error. Please try again</Alert>
            </Box>
          )}
          {step === STEPS.DEACTIVATION_REASON && (
            <DeactivationReason
              formattedExpirationDate={formattedExpirationDate}
              onReasonSelection={setReason}
              reason={reason}
              onFeedbackChange={handleFeedbackChange}
            />
          )}
          {step === STEPS.NOTIFY_LEADS && (
            <NotifyLeads
              leads={ghostedLeads}
              message={notAvailableMessage}
              setMessage={setNotAvailableMessage}
              toggleIncludeLead={toggleIncludeLead}
              listingAlias={listingAlias}
            />
          )}
          {(step === STEPS.CONGRATULATIONS || step === STEPS.THANK_YOU) && (
            <Congratulations
              step={step}
              launchedFrom={launchedFrom}
              setDisplayDeactivateModal={setDisplayDeactivateModal}
              canUpsellLeases={shouldShowLeasesSurvey}
            />
          )}

          {isCreateLeaseCTAStep && <CreateLease step={step} />}

          {isLeasesSurveyStep && (
            <LeasesSurvey
              step={step}
              onSelect={setLeaseSurveySelections}
              selectedOption={leaseSurveySelections[LEASES_SURVEY_CONTENT[step].DB_VALUE]}
            />
          )}

          {step === STEPS.CREATE_LEASE_ERROR && (
            <Box css={{ marginY: '800' }}>
              <Alert tone="critical">
                <Text>
                  There was an error creating or uploading your lease. Click&nbsp;
                  <Anchor href={`${paths.leasing}/property/${listingAlias}/onboarding/createLease`}>here</Anchor>
                  &nbsp;to manage your lease.
                </Text>
              </Alert>
            </Box>
          )}
        </div>
      }
      footer={
        !hideFooter && (
          <ModalFooter
            cancelText={cancelText}
            submitText={submitText}
            onSubmit={handleSubmit}
            onSubmitDataEventId={onSubmitDataEventId}
            onCancel={handleCancelClick}
            onCancelDataEventId={onCancelDataEventId}
            isSubmitDisabled={isNextButtonDisabled()}
            isSubmitting={isSubmitting}
          />
        )
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default DeactivationModal;
